import { useEffect, useState, type DragEvent, type FormEvent } from 'react';
import { ArrowLeft, Briefcase, Check, FileUp, MapPin, ShieldCheck, Upload, X } from 'lucide-react';
import { APPLY_FILES, COUNTRIES, CURRENCIES, WORK_AUTH } from '@ck/core';
import { ApiError } from '@ck/sdk';
import { api } from './api';
import { Logo } from '@ck/ui';
import type { PublicTenant } from '@ck/sdk';

/* eslint-disable @typescript-eslint/no-explicit-any */
type Job = { id: string; title: string; department: string; family: string; location: string; workplace: string; employment: string; salary: string; description: string; requirements: string; postedAt: string; closesOn: string | null };
const ZONES: string[] = (() => { try { return (Intl as any).supportedValuesOf('timeZone') as string[]; } catch { return ['Africa/Dar_es_Salaam', 'Africa/Nairobi', 'Africa/Lagos', 'Africa/Johannesburg', 'Europe/London', 'Asia/Dubai', 'UTC']; } })();
const LINKS = { linkedin: 'LinkedIn profile', github: 'GitHub / GitLab profile', myfxbook: 'MyFxBook (third-party verified track record)', portfolio: 'Portfolio, Behance or Figma link' } as const;

/** Public careers page: open jobs and a four-step application. Works without signing in. */
export function Careers({ tenant }: { tenant: PublicTenant }) {
  const [route, setRoute] = useState(() => location.hash);
  useEffect(() => { const f = () => setRoute(location.hash); window.addEventListener('hashchange', f); return () => window.removeEventListener('hashchange', f); }, []);
  const id = /^#careers\/([0-9a-f-]{36})/.exec(route)?.[1];
  return (
    <div className="careers">
      <header className="careers-top"><a href="#careers"><Logo branding={tenant.branding} on="dark" height={30} /></a><span>Careers</span></header>
      <main className="careers-main">{id ? <Apply id={id} /> : <JobList company={tenant.branding.name} />}</main>
    </div>
  );
}

function JobList({ company }: { company: string }) {
  const [d, setD] = useState<{ jobs: Job[] } | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.get<{ jobs: Job[] }>('/v1/careers').then(setD).catch((e) => setErr(e.message)); }, []);
  return (
    <>
      <h1>Work at {company}</h1>
      <p className="lead">We build trader education and evaluation across prop challenges, futures, perpetuals and prediction markets. Open roles are below.</p>
      {err && <p className="msg err">{err}</p>}
      {!d ? <p className="muted">Loading roles…</p> : d.jobs.length === 0 ? <p className="muted">No open roles right now. Check back soon.</p> : (
        <div className="careers-list">{d.jobs.map((j) => (
          <a key={j.id} className="careers-job" href={`#careers/${j.id}`}>
            <span><b>{j.title}</b><small><Briefcase />{[j.department, j.employment].filter(Boolean).join(' · ')}</small><small><MapPin />{[j.location, j.workplace].filter(Boolean).join(' · ')}</small></span>
            <span className="careers-go">{j.salary && <em>{j.salary}</em>}View & apply</span>
          </a>
        ))}</div>
      )}
    </>
  );
}

const STEPS = ['Personal & pay', 'Proof links', 'Documents', 'Confirm'];

function Apply({ id }: { id: string }) {
  const [d, setD] = useState<{ job: Job; needs: string[]; company: string } | null>(null);
  const [err, setErr] = useState('');
  const [step, setStep] = useState(0);
  const [f, setF] = useState<Record<string, string>>({ legalName: '', email: '', phone: '+', country: 'Tanzania', city: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, workAuth: WORK_AUTH[0], noticeDays: '30', startDate: '', salaryAmount: '', salaryCurrency: 'USD', salaryBasis: 'monthly', linkedin: '', github: '', myfxbook: '', journal: '', portfolio: '' });
  const [files, setFiles] = useState<Record<string, File>>({});
  const [attest, setAttest] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { api.get<any>(`/v1/careers/${id}`).then(setD).catch((e) => setErr(e.message)); }, [id]);
  if (err && !d) return <><a className="link back" href="#careers"><ArrowLeft />All roles</a><p className="msg err">{err}</p></>;
  if (!d) return <p className="muted">Loading…</p>;
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const required = (k: string) => d.needs.includes(k);
  const next = (e: FormEvent) => { e.preventDefault(); setErr(''); if (step === 2) { const miss = APPLY_FILES.filter((x) => x.required && !files[x.key]); if (miss.length) { setErr(`Add your ${miss.map((x) => x.label.toLowerCase()).join(', ')}.`); return; } } setStep(step + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!attest) { setErr('Tick the box to confirm your details are accurate.'); return; }
    setBusy(true); setErr('');
    const form = new FormData();
    Object.entries(f).forEach(([k, v]) => form.append(k, v));
    form.append('attest', 'yes');
    Object.entries(files).forEach(([k, v]) => form.append(k, v));
    try { await api.post(`/v1/careers/${id}/apply`, form); setDone(true); }
    catch (x) { setErr(x instanceof ApiError || x instanceof Error ? x.message : 'Something went wrong.'); }
    finally { setBusy(false); }
  }
  if (done) return (
    <div className="careers-done"><span className="ok-ring"><Check /></span><h1>Application sent</h1><p>Thank you, {(f.legalName ?? '').split(' ')[0]}. We received your application for <b>{d.job.title}</b> and sent a confirmation to {f.email}. Our team reviews every application and will contact you about next steps.</p><a className="btn" href="#careers">See other roles</a></div>
  );
  return (
    <>
      <a className="link back" href="#careers"><ArrowLeft />All roles</a>
      <h1>{d.job.title}</h1>
      <p className="muted">{[d.job.department, d.job.location, d.job.workplace, d.job.employment, d.job.salary].filter(Boolean).join(' · ')}</p>
      {step === 0 && <details className="careers-desc" open><summary>About the role</summary><p>{d.job.description}</p>{d.job.requirements && <><h3>Requirements</h3><p>{d.job.requirements}</p></>}</details>}
      <ol className="kyc-steps apply-steps">{STEPS.map((s, i) => <li key={s} className={i < step ? 'done' : i === step ? 'on' : ''}><i>{i < step ? '✓' : i + 1}</i><span>{s}</span></li>)}</ol>
      {err && <p className="msg err" role="alert">{err}</p>}

      {step === 0 && (
        <form className="form panel" onSubmit={next}>
          <h2>Personal & compensation</h2>
          <label>Full legal name <small>Exactly as on your government ID. No nicknames.</small><input required minLength={3} autoComplete="name" value={f.legalName} onChange={set('legalName')} /></label>
          <div className="row2">
            <label>Email<input type="email" required autoComplete="email" value={f.email} onChange={set('email')} /></label>
            <label>Phone <small>With country code, e.g. +255…</small><input type="tel" required pattern="\+[0-9 ]{7,}" autoComplete="tel" value={f.phone} onChange={set('phone')} /></label>
          </div>
          <div className="row2">
            <label>Country of residence<select value={f.country} onChange={set('country')}>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}</select></label>
            <label>City<input required value={f.city} onChange={set('city')} /></label>
          </div>
          <label>Time zone <small>We plan 24/7 rotating shifts across time zones.</small><select value={f.timezone} onChange={set('timezone')}>{ZONES.map((z) => <option key={z}>{z}</option>)}</select></label>
          <label>Work authorisation<select value={f.workAuth} onChange={set('workAuth')}>{WORK_AUTH.map((w) => <option key={w}>{w}</option>)}</select></label>
          <div className="row2">
            <label>Notice period (days)<input type="number" min={0} max={365} required value={f.noticeDays} onChange={set('noticeDays')} /></label>
            <label>Earliest start date <small>Optional</small><input type="date" value={f.startDate} onChange={set('startDate')} /></label>
          </div>
          <div className="row3">
            <label>Salary expectation<input type="number" min={1} required value={f.salaryAmount} onChange={set('salaryAmount')} /></label>
            <label>Currency<select value={f.salaryCurrency} onChange={set('salaryCurrency')}>{CURRENCIES.map((c) => <option key={c}>{c}</option>)}</select></label>
            <label>Per<select value={f.salaryBasis} onChange={set('salaryBasis')}><option value="monthly">Month (gross)</option><option value="annual">Year (gross)</option><option value="hourly">Hour (contractor)</option></select></label>
          </div>
          <div className="row-a"><button className="btn">Continue</button></div>
        </form>
      )}

      {step === 1 && (
        <form className="form panel" onSubmit={next}>
          <h2>Proof links</h2>
          <p className="fine">Working links that verify your experience. Required ones depend on the role.</p>
          {(Object.keys(LINKS) as (keyof typeof LINKS)[]).map((k) => (k === 'linkedin' || required(k) || f[k]) || k === 'github' || k === 'myfxbook' || k === 'portfolio' ? (
            <label key={k}>{LINKS[k]} {k === 'linkedin' || required(k) ? <small>Required</small> : <small>Optional</small>}<input type="url" required={k === 'linkedin' || required(k)} pattern="https?://.+" placeholder="https://" value={f[k]} onChange={set(k)} /></label>
          ) : null)}
          {d.job.family === 'trading' && <label>Public or audited trading journal <small>Optional: drawdown and consistency history</small><input type="url" pattern="https?://.+" placeholder="https://" value={f.journal} onChange={set('journal')} /></label>}
          <div className="row-a"><button type="button" className="link" onClick={() => setStep(0)}>Back</button><button className="btn">Continue</button></div>
        </form>
      )}

      {step === 2 && (
        <form className="form panel" onSubmit={next}>
          <h2>Documents</h2>
          <p className="fine">PDF, PNG or JPEG, up to 10 MB each. Your CV must be a PDF.</p>
          <div className="drop-grid">{APPLY_FILES.map((x) => <Drop key={x.key} spec={x} file={files[x.key] ?? null} onFile={(file) => setFiles((cur) => { const n = { ...cur }; if (file) n[x.key] = file; else delete n[x.key]; return n; })} onError={setErr} />)}</div>
          <div className="row-a"><button type="button" className="link" onClick={() => setStep(1)}>Back</button><button className="btn">Continue</button></div>
        </form>
      )}

      {step === 3 && (
        <form className="form panel" onSubmit={submit}>
          <h2>Confirm and send</h2>
          <dl className="facts">
            <div><dt>Name</dt><dd>{f.legalName}</dd></div><div><dt>Contact</dt><dd>{f.email} · {f.phone}</dd></div>
            <div><dt>Location</dt><dd>{f.city}, {f.country} ({f.timezone})</dd></div><div><dt>Work authorisation</dt><dd>{f.workAuth}</dd></div>
            <div><dt>Availability</dt><dd>{f.noticeDays} days’ notice{f.startDate ? `, from ${f.startDate}` : ''}</dd></div><div><dt>Expectation</dt><dd>{f.salaryCurrency} {Number(f.salaryAmount).toLocaleString()} per {f.salaryBasis === 'annual' ? 'year' : f.salaryBasis === 'hourly' ? 'hour' : 'month'}</dd></div>
            <div><dt>Documents</dt><dd>{Object.keys(files).length} uploaded</dd></div>
          </dl>
          <label className="check"><input type="checkbox" checked={attest} onChange={(e) => setAttest(e.target.checked)} /><span><ShieldCheck /> I confirm the information and documents I have given are true, complete and my own. I understand that false information ends the application or any employment, and that {d.company} may verify my references, identity and history.</span></label>
          <div className="row-a"><button type="button" className="link" onClick={() => setStep(2)}>Back</button><button className="btn" disabled={busy || !attest}>{busy ? 'Sending…' : 'Submit application'}</button></div>
        </form>
      )}
    </>
  );
}

function Drop({ spec, file, onFile, onError }: { spec: (typeof APPLY_FILES)[number]; file: File | null; onFile: (f: File | null) => void; onError: (m: string) => void }) {
  const [over, setOver] = useState(false);
  const accept = 'pdfOnly' in spec && spec.pdfOnly ? ['application/pdf'] : ['application/pdf', 'image/png', 'image/jpeg'];
  const take = (f: File | undefined) => {
    if (!f) return;
    if (!accept.includes(f.type)) { onError(`${spec.label}: use ${accept.length === 1 ? 'a PDF' : 'a PDF, PNG or JPEG'}.`); return; }
    if (f.size > 10 * 1024 * 1024) { onError(`${spec.label}: the file is over 10 MB.`); return; }
    onError(''); onFile(f);
  };
  const drop = (e: DragEvent) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); };
  return (
    <label className={`drop${over ? ' over' : ''}${file ? ' has' : ''}`} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop}>
      <input type="file" accept={accept.join(',')} hidden onChange={(e) => take(e.target.files?.[0])} />
      {file ? <FileUp /> : <Upload />}
      <b>{spec.label}{spec.required ? '' : ' (optional)'}</b>
      <small>{file ? `${file.name} · ${(file.size / 1024 / 1024).toFixed(1)} MB` : spec.help}</small>
      {file && <button type="button" className="x" aria-label={`Remove ${spec.label}`} onClick={(e) => { e.preventDefault(); onFile(null); }}><X /></button>}
    </label>
  );
}
